import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Client, clientsAPI, pipelineAPI, Pipeline, PipelineStage } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import {
  PlusIcon,
  MinusIcon,
  ArrowLeftIcon,
  BuildingOfficeIcon,
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  BuildingStorefrontIcon,
  GlobeAltIcon,
  StarIcon,
} from '@heroicons/react/24/outline';
import { Card, CardBody, Button, Spinner, Badge } from '../ui';

interface ClientFormProps {
  isEdit?: boolean;
}

const ClientForm: React.FC<ClientFormProps> = ({ isEdit = false }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Pipeline state
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [stages, setStages] = useState<PipelineStage[]>([]);
  const [selectedPipelineId, setSelectedPipelineId] = useState<number | undefined>(undefined);
  const [selectedStageId, setSelectedStageId] = useState<number | undefined>(undefined);

  // Check if we're in admin context
  const isAdminContext = location.pathname.startsWith('/admin');

  // Helper function to generate correct paths
  const getClientPath = (path: string) => {
    return isAdminContext ? `/admin${path}` : path;
  };
  const [formData, setFormData] = useState({
    // Basic Info
    name: '',
    type: 'COMPANY' as Client['type'],
    email: '',
    additionalEmails: [] as string[],
    phoneNumbers: [''],
    address: '',
    importance: 'MEDIUM' as Client['importance'],
    notes: '',
    assignedUserId: undefined as number | undefined,

    // Lifecycle & Status (Phase 1.1)
    lifecycleStage: 'LEAD' as Client['lifecycleStage'],
    leadStatus: 'NEW' as Client['leadStatus'],
    leadSource: '',

    // Company/Professional Info (Phase 1.1)
    industry: '',
    companySize: undefined as number | undefined,
    annualRevenue: undefined as number | undefined,
    jobTitle: '',
    department: '',
    isDecisionMaker: false,

    // Contact Management (Phase 1.1)
    lastContactDate: '',
    nextFollowUpDate: '',
    leadScore: 0,
    tags: [] as string[],

    // Additional Contact Info (Phase 1.1)
    website: '',
    linkedinUrl: '',
    twitterUrl: '',
    facebookUrl: '',
    preferredContactMethod: '',
    timeZone: '',
    languagePreference: 'ARABIC' as Client['languagePreference'],

    // Compliance (Phase 1.1)
    doNotContact: false,
    gdprConsent: false,
    gdprConsentDate: '',
  });

  useEffect(() => {
    fetchPipelines();
    if (isEdit && id) {
      fetchClient();
    }
  }, [isEdit, id]);

  // Load stages when pipeline changes
  useEffect(() => {
    if (selectedPipelineId) {
      fetchStages(selectedPipelineId);
    } else {
      setStages([]);
      setSelectedStageId(undefined);
    }
  }, [selectedPipelineId]);

  const fetchPipelines = async () => {
    try {
      const data = await pipelineAPI.getAll();
      setPipelines(data);
    } catch (err) {
      console.error('Failed to fetch pipelines:', err);
    }
  };

  const fetchStages = async (pipelineId: number) => {
    try {
      const data = await pipelineAPI.getStages(pipelineId);
      setStages(data);
    } catch (err) {
      console.error('Failed to fetch stages:', err);
    }
  };

  const fetchClient = async () => {
    try {
      setLoading(true);
      const client = await clientsAPI.getById(Number(id));

      // Set pipeline and stage
      if (client.pipelineId) {
        setSelectedPipelineId(client.pipelineId);
        // Fetch stages for this pipeline
        const stagesData = await pipelineAPI.getStages(client.pipelineId);
        setStages(stagesData);
      }
      if (client.stageId) {
        setSelectedStageId(client.stageId);
      }

      setFormData({
        // Basic Info
        name: client.name,
        type: client.type,
        email: client.email || '',
        additionalEmails: client.additionalEmails || [],
        phoneNumbers: client.phoneNumbers.length > 0 ? client.phoneNumbers : [''],
        address: client.address || '',
        importance: client.importance,
        notes: client.notes || '',
        assignedUserId: client.assignedUserId || undefined,

        // Lifecycle & Status
        lifecycleStage: client.lifecycleStage || 'LEAD',
        leadStatus: client.leadStatus || 'NEW',
        leadSource: client.leadSource || '',

        // Company/Professional Info
        industry: client.industry || '',
        companySize: client.companySize || undefined,
        annualRevenue: client.annualRevenue ? Number(client.annualRevenue) : undefined,
        jobTitle: client.jobTitle || '',
        department: client.department || '',
        isDecisionMaker: client.isDecisionMaker || false,

        // Contact Management
        lastContactDate: client.lastContactDate ? client.lastContactDate.split('T')[0] : '',
        nextFollowUpDate: client.nextFollowUpDate ? client.nextFollowUpDate.split('T')[0] : '',
        leadScore: client.leadScore || 0,
        tags: client.tags || [],

        // Additional Contact Info
        website: client.website || '',
        linkedinUrl: client.linkedinUrl || '',
        twitterUrl: client.twitterUrl || '',
        facebookUrl: client.facebookUrl || '',
        preferredContactMethod: client.preferredContactMethod || '',
        timeZone: client.timeZone || '',
        languagePreference: client.languagePreference || 'ARABIC',

        // Compliance
        doNotContact: client.doNotContact || false,
        gdprConsent: client.gdprConsent || false,
        gdprConsentDate: client.gdprConsentDate ? client.gdprConsentDate.split('T')[0] : '',
      });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch client');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;

    let processedValue: any = value;

    // Handle number fields
    if (name === 'assignedUserId' || name === 'companySize' || name === 'annualRevenue' || name === 'leadScore') {
      processedValue = value ? Number(value) : undefined;
    }
    // Handle checkbox fields
    else if (type === 'checkbox') {
      processedValue = (e.target as HTMLInputElement).checked;
    }

    setFormData(prev => ({
      ...prev,
      [name]: processedValue,
    }));

    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handlePhoneChange = (index: number, value: string) => {
    const newPhones = [...formData.phoneNumbers];
    newPhones[index] = value;
    setFormData(prev => ({ ...prev, phoneNumbers: newPhones }));
    // Clear phone errors when user starts typing
    if (fieldErrors.phoneNumbers) {
      setFieldErrors(prev => ({ ...prev, phoneNumbers: '' }));
    }
  };

  const handleAdditionalEmailChange = (index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      additionalEmails: prev.additionalEmails.map((email, emailIndex) => (
        emailIndex === index ? value : email
      )),
    }));
    if (fieldErrors.additionalEmails) {
      setFieldErrors(prev => ({ ...prev, additionalEmails: '' }));
    }
  };

  const addAdditionalEmail = () => {
    setFormData(prev => ({
      ...prev,
      additionalEmails: [...prev.additionalEmails, ''],
    }));
  };

  const removeAdditionalEmail = (index: number) => {
    setFormData(prev => ({
      ...prev,
      additionalEmails: prev.additionalEmails.filter((_, emailIndex) => emailIndex !== index),
    }));
  };

  const makePrimaryEmail = (index: number) => {
    setFormData(prev => {
      const nextPrimary = prev.additionalEmails[index]?.trim();
      if (!nextPrimary) return prev;

      const nextAdditional = prev.additionalEmails.filter((_, emailIndex) => emailIndex !== index);
      if (prev.email.trim()) {
        nextAdditional.unshift(prev.email.trim());
      }

      return {
        ...prev,
        email: nextPrimary,
        additionalEmails: nextAdditional,
      };
    });
    setFieldErrors(prev => ({ ...prev, email: '', additionalEmails: '' }));
  };

  const addPhoneNumber = () => {
    setFormData(prev => ({
      ...prev,
      phoneNumbers: [...prev.phoneNumbers, ''],
    }));
  };

  const removePhoneNumber = (index: number) => {
    if (formData.phoneNumbers.length > 1) {
      const newPhones = formData.phoneNumbers.filter((_, i) => i !== index);
      setFormData(prev => ({ ...prev, phoneNumbers: newPhones }));
    }
  };

  // Handle tags
  const [tagInput, setTagInput] = useState('');

  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()],
      }));
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove),
    }));
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Client name is required';
    }
    
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    const additionalEmails = formData.additionalEmails
      .map(email => email.trim())
      .filter(Boolean);
    if (!formData.email.trim() && additionalEmails.length > 0) {
      errors.additionalEmails = 'Add a primary email before adding additional emails';
    } else if (additionalEmails.some(email => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))) {
      errors.additionalEmails = 'Please enter valid additional email addresses';
    } else {
      const allEmails = [formData.email.trim(), ...additionalEmails]
        .filter(Boolean)
        .map(email => email.toLowerCase());
      if (new Set(allEmails).size !== allEmails.length) {
        errors.additionalEmails = 'Email addresses must be unique';
      }
    }
    
    const validPhones = formData.phoneNumbers.filter(phone => phone.trim() !== '');
    if (validPhones.length === 0) {
      errors.phoneNumbers = 'At least one phone number is required';
    }
    
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!validateForm()) {
      return;
    }

    setSaving(true);

    try {
      const submitData = {
        ...formData,
        email: formData.email.trim(),
        additionalEmails: formData.additionalEmails
          .map(email => email.trim())
          .filter(Boolean),
        phoneNumbers: formData.phoneNumbers.filter(phone => phone.trim() !== ''),
        pipelineId: selectedPipelineId,
        stageId: selectedStageId,
      };

      if (isEdit && id) {
        await clientsAPI.update(Number(id), submitData);
        setSuccess('Client updated successfully!');
      } else {
        await clientsAPI.create(submitData);
        setSuccess('Client created successfully!');
      }

      setTimeout(() => navigate(getClientPath('/clients')), 1500);
    } catch (err: any) {
      setError(err.response?.data?.message || `Failed to ${isEdit ? 'update' : 'create'} client`);
    } finally {
      setSaving(false);
    }
  };

  const getClientTypeIcon = (type: string) => {
    switch (type) {
      case 'COMPANY':
        return <BuildingOfficeIcon className="h-5 w-5 text-primary-500" />;
      case 'STORE':
        return <BuildingStorefrontIcon className="h-5 w-5 text-success-500" />;
      case 'PERSON':
        return <UserIcon className="h-5 w-5 text-secondary-500" />;
      case 'ORGANIZATION':
        return <GlobeAltIcon className="h-5 w-5 text-warning-500" />;
      default:
        return <BuildingOfficeIcon className="h-5 w-5 text-neutral-500" />;
    }
  };

  const getImportanceColor = (importance: string) => {
    switch (importance) {
      case 'VERY_LOW':
        return 'text-neutral-600 bg-neutral-100 border-neutral-300';
      case 'LOW':
        return 'text-primary-600 bg-primary-100 border-primary-300';
      case 'MEDIUM':
        return 'text-warning-600 bg-warning-100 border-warning-300';
      case 'HIGH':
        return 'text-warning-600 bg-warning-100 border-warning-300';
      case 'VERY_HIGH':
        return 'text-error-600 bg-error-100 border-error-300';
      default:
        return 'text-neutral-600 bg-neutral-100 border-neutral-300';
    }
  };

  if (loading && isEdit) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="text-center">
          <Spinner size="lg" />
          <p className="mt-4 text-sm text-neutral-600">Loading client information...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate(getClientPath('/clients'))}
            className="mb-4"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-1" />
            Back to Clients
          </Button>

          <Card variant="gradient" className="overflow-hidden">
            <CardBody className="p-6">
              <div className="flex items-center space-x-4">
                <div className="flex-shrink-0">
                  <div className="h-16 w-16 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center shadow-sm">
                    {getClientTypeIcon(formData.type)}
                  </div>
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-neutral-900">
                    {isEdit ? 'Edit Client' : 'Create New Client'}
                  </h1>
                  <p className="text-sm text-neutral-600 mt-1">
                    {isEdit ? 'Update client information and settings' : 'Add a new client to your portfolio'}
                  </p>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        {/* Success Message */}
        {success && (
          <Card variant="bordered" className="mb-6 border-success-200 bg-success-50">
            <CardBody className="p-4">
              <div className="flex items-center">
                <CheckCircleIcon className="h-5 w-5 text-success-500 mr-3" />
                <p className="text-sm font-medium text-success-800">{success}</p>
              </div>
            </CardBody>
          </Card>
        )}

        {/* Error Message */}
        {error && (
          <Card variant="bordered" className="mb-6 border-error-200 bg-error-50">
            <CardBody className="p-4">
              <div className="flex items-center">
                <ExclamationTriangleIcon className="h-5 w-5 text-error-500 mr-3" />
                <p className="text-sm font-medium text-error-800">{error}</p>
              </div>
            </CardBody>
          </Card>
        )}

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic Information Card */}
          <Card variant="bordered">
            <CardBody className="p-6 sm:p-8">
              <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                <div className="sm:col-span-6">
                  <h3 className="text-lg font-semibold leading-7 text-neutral-900 flex items-center">
                    <BuildingOfficeIcon className="h-5 w-5 mr-2 text-neutral-400" />
                    Basic Information
                  </h3>
                  <p className="mt-1 text-sm leading-6 text-neutral-600">
                    Essential details about the client
                  </p>
                </div>

                {/* Client Name */}
                <div className="sm:col-span-4">
                  <label htmlFor="name" className="block text-sm font-medium leading-6 text-neutral-900">
                    Client Name *
                  </label>
                  <div className="mt-2">
                    <input
                      type="text"
                      name="name"
                      id="name"
                      required
                      className={`block w-full rounded-lg border-0 py-2.5 px-3 text-neutral-900 shadow-sm ring-1 ring-inset placeholder:text-neutral-400 focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6 transition-colors ${
                        fieldErrors.name
                          ? 'ring-error-300 focus:ring-error-500'
                          : 'ring-neutral-300 focus:ring-primary-600'
                      }`}
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Enter client name"
                    />
                    {fieldErrors.name && (
                      <p className="mt-2 text-sm text-error-600 flex items-center">
                        <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                        {fieldErrors.name}
                      </p>
                    )}
                  </div>
                </div>

                {/* Client Type */}
                <div className="sm:col-span-2">
                  <label htmlFor="type" className="block text-sm font-medium leading-6 text-neutral-900">
                    Type *
                  </label>
                  <div className="mt-2">
                    <select
                      name="type"
                      id="type"
                      required
                      className="block w-full rounded-lg border-0 py-2.5 px-3 text-neutral-900 shadow-sm ring-1 ring-inset ring-neutral-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                      value={formData.type}
                      onChange={handleChange}
                    >
                      <option value="COMPANY">Company</option>
                      <option value="ORGANIZATION">Organization</option>
                      <option value="STORE">Store</option>
                      <option value="PERSON">Person</option>
                      <option value="OTHER">Other</option>
                    </select>
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Contact Information Card */}
          <Card variant="bordered">
            <CardBody className="p-6 sm:p-8">
              <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                <div className="sm:col-span-6">
                  <h3 className="text-lg font-semibold leading-7 text-neutral-900 flex items-center">
                    <EnvelopeIcon className="h-5 w-5 mr-2 text-neutral-400" />
                    Contact Information
                  </h3>
                  <p className="mt-1 text-sm leading-6 text-neutral-600">
                    How to reach this client
                  </p>
                </div>

                {/* Email addresses */}
                <div className="sm:col-span-3">
                  <label htmlFor="email" className="block text-sm font-medium leading-6 text-neutral-900">
                    Primary Email
                  </label>
                  <div className="mt-2 space-y-3">
                    <div className="flex rounded-md shadow-sm ring-1 ring-inset ring-neutral-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-primary-600">
                      <span className="flex select-none items-center pl-3 text-neutral-500 sm:text-sm">
                        <EnvelopeIcon className="h-4 w-4" />
                      </span>
                      <input
                        type="email"
                        name="email"
                        id="email"
                        className={`block flex-1 border-0 bg-transparent py-2.5 pl-2 text-neutral-900 placeholder:text-neutral-400 focus:ring-0 sm:text-sm sm:leading-6 ${
                          fieldErrors.email ? 'text-error-900' : ''
                        }`}
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="client@example.com"
                      />
                    </div>
                    {formData.additionalEmails.map((email, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <div className="flex min-w-0 flex-1 rounded-md shadow-sm ring-1 ring-inset ring-neutral-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-primary-600">
                          <span className="flex select-none items-center pl-3 text-neutral-500 sm:text-sm">
                            <EnvelopeIcon className="h-4 w-4" />
                          </span>
                          <input
                            type="email"
                            aria-label={`Additional email ${index + 1}`}
                            value={email}
                            onChange={(event) => handleAdditionalEmailChange(index, event.target.value)}
                            className="block min-w-0 flex-1 border-0 bg-transparent py-2.5 pl-2 text-neutral-900 placeholder:text-neutral-400 focus:ring-0 sm:text-sm sm:leading-6"
                            placeholder="additional@example.com"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => makePrimaryEmail(index)}
                          disabled={!email.trim()}
                          title="Set as primary email"
                          aria-label={`Set ${email || `email ${index + 1}`} as primary`}
                          className="rounded-md p-2 text-primary-600 transition-colors hover:bg-primary-50 disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          <StarIcon className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => removeAdditionalEmail(index)}
                          title="Remove email"
                          aria-label={`Remove additional email ${index + 1}`}
                          className="rounded-md p-2 text-error-600 transition-colors hover:bg-error-50"
                        >
                          <MinusIcon className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                    {fieldErrors.email && (
                      <p className="mt-2 text-sm text-error-600 flex items-center">
                        <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                        {fieldErrors.email}
                      </p>
                    )}
                    {fieldErrors.additionalEmails && (
                      <p className="text-sm text-error-600 flex items-center">
                        <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                        {fieldErrors.additionalEmails}
                      </p>
                    )}
                    <button
                      type="button"
                      onClick={addAdditionalEmail}
                      className="inline-flex items-center rounded-md border border-primary-200 bg-primary-50 px-3 py-2 text-sm font-medium text-primary-700 transition-colors hover:bg-primary-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                    >
                      <PlusIcon className="mr-1 h-4 w-4" />
                      Add Email
                    </button>
                  </div>
                </div>

                {/* Phone Numbers */}
                <div className="sm:col-span-3">
                  <label className="block text-sm font-medium leading-6 text-neutral-900">
                    Phone Numbers *
                  </label>
                  <div className="mt-2 space-y-3">
                    {formData.phoneNumbers.map((phone, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <div className="flex-1 flex rounded-md shadow-sm ring-1 ring-inset ring-neutral-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-primary-600">
                          <span className="flex select-none items-center pl-3 text-neutral-500 sm:text-sm">
                            <PhoneIcon className="h-4 w-4" />
                          </span>
                          <input
                            type="tel"
                            value={phone}
                            onChange={(e) => handlePhoneChange(index, e.target.value)}
                            className="block flex-1 border-0 bg-transparent py-2.5 pl-2 text-neutral-900 placeholder:text-neutral-400 focus:ring-0 sm:text-sm sm:leading-6"
                            placeholder="+1 (555) 123-4567"
                          />
                        </div>
                        {formData.phoneNumbers.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removePhoneNumber(index)}
                            className="inline-flex items-center p-2 text-error-600 hover:text-error-800 hover:bg-error-50 rounded-md transition-colors"
                          >
                            <MinusIcon className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    ))}
                    {fieldErrors.phoneNumbers && (
                      <p className="text-sm text-error-600 flex items-center">
                        <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                        {fieldErrors.phoneNumbers}
                      </p>
                    )}
                    <button
                      type="button"
                      onClick={addPhoneNumber}
                      className="inline-flex items-center px-3 py-2 text-sm font-medium text-primary-700 bg-primary-50 border border-primary-200 rounded-md hover:bg-primary-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
                    >
                      <PlusIcon className="h-4 w-4 mr-1" />
                      Add Phone Number
                    </button>
                  </div>
                </div>

                {/* Address */}
                <div className="sm:col-span-6">
                  <label htmlFor="address" className="block text-sm font-medium leading-6 text-neutral-900">
                    Address
                  </label>
                  <div className="mt-2">
                    <div className="flex rounded-md shadow-sm ring-1 ring-inset ring-neutral-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-primary-600">
                      <span className="flex select-none items-start pt-3 pl-3 text-neutral-500 sm:text-sm">
                        <MapPinIcon className="h-4 w-4" />
                      </span>
                      <textarea
                        name="address"
                        id="address"
                        rows={3}
                        className="block flex-1 border-0 bg-transparent py-2.5 pl-2 text-neutral-900 placeholder:text-neutral-400 focus:ring-0 sm:text-sm sm:leading-6 resize-none"
                        value={formData.address}
                        onChange={handleChange}
                        placeholder="123 Main St, City, State 12345"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Pipeline Stage Card */}
          <Card variant="bordered">
            <CardBody className="p-6 sm:p-8">
              <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                <div className="sm:col-span-6">
                  <h3 className="text-lg font-semibold leading-7 text-neutral-900">
                    Pipeline Stage
                  </h3>
                  <p className="mt-1 text-sm leading-6 text-neutral-600">
                    Assign the client to a sales pipeline and stage
                  </p>
                </div>

                {/* Pipeline */}
                <div className="sm:col-span-3">
                  <label htmlFor="pipelineId" className="block text-sm font-medium leading-6 text-neutral-900">
                    Pipeline
                  </label>
                  <div className="mt-2">
                    <select
                      name="pipelineId"
                      id="pipelineId"
                      className="block w-full rounded-lg border-0 py-2.5 px-3 text-neutral-900 shadow-sm ring-1 ring-inset ring-neutral-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                      value={selectedPipelineId || ''}
                      onChange={(e) => {
                        const value = e.target.value ? parseInt(e.target.value) : undefined;
                        setSelectedPipelineId(value);
                        setSelectedStageId(undefined);
                      }}
                    >
                      <option value="">Select a pipeline</option>
                      {pipelines.map((pipeline) => (
                        <option key={pipeline.id} value={pipeline.id}>
                          {pipeline.name} {pipeline.isDefault ? '(Default)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Stage */}
                <div className="sm:col-span-3">
                  <label htmlFor="stageId" className="block text-sm font-medium leading-6 text-neutral-900">
                    Stage
                  </label>
                  <div className="mt-2">
                    <select
                      name="stageId"
                      id="stageId"
                      className="block w-full rounded-lg border-0 py-2.5 px-3 text-neutral-900 shadow-sm ring-1 ring-inset ring-neutral-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                      value={selectedStageId || ''}
                      onChange={(e) => {
                        const value = e.target.value ? parseInt(e.target.value) : undefined;
                        setSelectedStageId(value);
                      }}
                      disabled={!selectedPipelineId}
                    >
                      <option value="">Select a stage</option>
                      {stages.map((stage) => (
                        <option key={stage.id} value={stage.id}>
                          {stage.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  {!selectedPipelineId && (
                    <p className="mt-1 text-xs text-neutral-500">Select a pipeline first</p>
                  )}
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Lifecycle & Status Card */}
          <Card variant="bordered">
            <CardBody className="p-6 sm:p-8">
              <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                <div className="sm:col-span-6">
                  <h3 className="text-lg font-semibold leading-7 text-neutral-900">
                    Lifecycle & Status
                  </h3>
                  <p className="mt-1 text-sm leading-6 text-neutral-600">
                    Track the client's journey and current status
                  </p>
                </div>

                {/* Lifecycle Stage */}
                <div className="sm:col-span-2">
                  <label htmlFor="lifecycleStage" className="block text-sm font-medium leading-6 text-neutral-900">
                    Lifecycle Stage
                  </label>
                  <div className="mt-2">
                    <select
                      name="lifecycleStage"
                      id="lifecycleStage"
                      className="block w-full rounded-md border-0 py-2.5 px-3 text-neutral-900 shadow-sm ring-1 ring-inset ring-neutral-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                      value={formData.lifecycleStage}
                      onChange={handleChange}
                    >
                      <option value="SUBSCRIBER">Subscriber</option>
                      <option value="LEAD">Lead</option>
                      <option value="MQL">Marketing Qualified Lead</option>
                      <option value="SQL">Sales Qualified Lead</option>
                      <option value="OPPORTUNITY">Opportunity</option>
                      <option value="CUSTOMER">Customer</option>
                      <option value="EVANGELIST">Evangelist</option>
                      <option value="OTHER">Other</option>
                    </select>
                  </div>
                </div>

                {/* Lead Status */}
                <div className="sm:col-span-2">
                  <label htmlFor="leadStatus" className="block text-sm font-medium leading-6 text-neutral-900">
                    Lead Status
                  </label>
                  <div className="mt-2">
                    <select
                      name="leadStatus"
                      id="leadStatus"
                      className="block w-full rounded-md border-0 py-2.5 px-3 text-neutral-900 shadow-sm ring-1 ring-inset ring-neutral-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                      value={formData.leadStatus}
                      onChange={handleChange}
                    >
                      <option value="NEW">New</option>
                      <option value="ATTEMPTING_TO_CONTACT">Attempting to Contact</option>
                      <option value="CONNECTED">Connected</option>
                      <option value="OPEN_DEAL">Open Deal</option>
                      <option value="UNQUALIFIED">Unqualified</option>
                      <option value="BAD_TIMING">Bad Timing</option>
                    </select>
                  </div>
                </div>

                {/* Lead Source */}
                <div className="sm:col-span-2">
                  <label htmlFor="leadSource" className="block text-sm font-medium leading-6 text-neutral-900">
                    Lead Source
                  </label>
                  <div className="mt-2">
                    <input
                      type="text"
                      name="leadSource"
                      id="leadSource"
                      className="block w-full rounded-md border-0 py-2.5 px-3 text-neutral-900 shadow-sm ring-1 ring-inset ring-neutral-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                      value={formData.leadSource}
                      onChange={handleChange}
                      placeholder="e.g., Website, Referral"
                    />
                  </div>
                </div>

                {/* Last Contact Date */}
                <div className="sm:col-span-3">
                  <label htmlFor="lastContactDate" className="block text-sm font-medium leading-6 text-neutral-900">
                    Last Contact Date
                  </label>
                  <div className="mt-2">
                    <input
                      type="date"
                      name="lastContactDate"
                      id="lastContactDate"
                      className="block w-full rounded-md border-0 py-2.5 px-3 text-neutral-900 shadow-sm ring-1 ring-inset ring-neutral-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                      value={formData.lastContactDate}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                {/* Next Follow-up Date */}
                <div className="sm:col-span-3">
                  <label htmlFor="nextFollowUpDate" className="block text-sm font-medium leading-6 text-neutral-900">
                    Next Follow-up Date
                  </label>
                  <div className="mt-2">
                    <input
                      type="date"
                      name="nextFollowUpDate"
                      id="nextFollowUpDate"
                      className="block w-full rounded-md border-0 py-2.5 px-3 text-neutral-900 shadow-sm ring-1 ring-inset ring-neutral-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                      value={formData.nextFollowUpDate}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                {/* Lead Score */}
                <div className="sm:col-span-2">
                  <label htmlFor="leadScore" className="block text-sm font-medium leading-6 text-neutral-900">
                    Lead Score
                  </label>
                  <div className="mt-2">
                    <input
                      type="number"
                      name="leadScore"
                      id="leadScore"
                      min="0"
                      max="100"
                      className="block w-full rounded-md border-0 py-2.5 px-3 text-neutral-900 shadow-sm ring-1 ring-inset ring-neutral-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                      value={formData.leadScore}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                {/* Tags */}
                <div className="sm:col-span-4">
                  <label htmlFor="tags" className="block text-sm font-medium leading-6 text-neutral-900">
                    Tags
                  </label>
                  <div className="mt-2 flex gap-2">
                    <input
                      type="text"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                      className="block w-full rounded-md border-0 py-2.5 px-3 text-neutral-900 shadow-sm ring-1 ring-inset ring-neutral-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                      placeholder="Add a tag and press Enter"
                    />
                    <button
                      type="button"
                      onClick={addTag}
                      className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
                    >
                      Add
                    </button>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {formData.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-800"
                      >
                        {tag}
                        <button
                          type="button"
                          onClick={() => removeTag(tag)}
                          className="ml-2 text-primary-600 hover:text-primary-800"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Company/Professional Info Card */}
          <Card variant="bordered">
            <CardBody className="p-6 sm:p-8">
              <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                <div className="sm:col-span-6">
                  <h3 className="text-lg font-semibold leading-7 text-neutral-900">
                    Company & Professional Information
                  </h3>
                  <p className="mt-1 text-sm leading-6 text-neutral-600">
                    Business details and professional information
                  </p>
                </div>

                {/* Industry */}
                <div className="sm:col-span-3">
                  <label htmlFor="industry" className="block text-sm font-medium leading-6 text-neutral-900">
                    Industry
                  </label>
                  <div className="mt-2">
                    <input
                      type="text"
                      name="industry"
                      id="industry"
                      className="block w-full rounded-lg border-0 py-2.5 px-3 text-neutral-900 shadow-sm ring-1 ring-inset ring-neutral-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                      value={formData.industry}
                      onChange={handleChange}
                      placeholder="e.g., Technology, Healthcare"
                    />
                  </div>
                </div>

                {/* Company Size */}
                <div className="sm:col-span-3">
                  <label htmlFor="companySize" className="block text-sm font-medium leading-6 text-neutral-900">
                    Company Size
                  </label>
                  <div className="mt-2">
                    <input
                      type="number"
                      name="companySize"
                      id="companySize"
                      min="1"
                      className="block w-full rounded-md border-0 py-2.5 px-3 text-neutral-900 shadow-sm ring-1 ring-inset ring-neutral-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                      value={formData.companySize || ''}
                      onChange={handleChange}
                      placeholder="Number of employees"
                    />
                  </div>
                </div>

                {/* Annual Revenue */}
                <div className="sm:col-span-3">
                  <label htmlFor="annualRevenue" className="block text-sm font-medium leading-6 text-neutral-900">
                    Annual Revenue ($)
                  </label>
                  <div className="mt-2">
                    <input
                      type="number"
                      name="annualRevenue"
                      id="annualRevenue"
                      min="0"
                      step="0.01"
                      className="block w-full rounded-md border-0 py-2.5 px-3 text-neutral-900 shadow-sm ring-1 ring-inset ring-neutral-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                      value={formData.annualRevenue || ''}
                      onChange={handleChange}
                      placeholder="Annual revenue"
                    />
                  </div>
                </div>

                {/* Job Title */}
                <div className="sm:col-span-3">
                  <label htmlFor="jobTitle" className="block text-sm font-medium leading-6 text-neutral-900">
                    Job Title
                  </label>
                  <div className="mt-2">
                    <input
                      type="text"
                      name="jobTitle"
                      id="jobTitle"
                      className="block w-full rounded-md border-0 py-2.5 px-3 text-neutral-900 shadow-sm ring-1 ring-inset ring-neutral-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                      value={formData.jobTitle}
                      onChange={handleChange}
                      placeholder="e.g., CEO, Manager"
                    />
                  </div>
                </div>

                {/* Department */}
                <div className="sm:col-span-3">
                  <label htmlFor="department" className="block text-sm font-medium leading-6 text-neutral-900">
                    Department
                  </label>
                  <div className="mt-2">
                    <input
                      type="text"
                      name="department"
                      id="department"
                      className="block w-full rounded-md border-0 py-2.5 px-3 text-neutral-900 shadow-sm ring-1 ring-inset ring-neutral-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                      value={formData.department}
                      onChange={handleChange}
                      placeholder="e.g., Sales, Marketing"
                    />
                  </div>
                </div>

                {/* Is Decision Maker */}
                <div className="sm:col-span-3">
                  <div className="flex items-center h-full pt-8">
                    <input
                      type="checkbox"
                      name="isDecisionMaker"
                      id="isDecisionMaker"
                      checked={formData.isDecisionMaker}
                      onChange={handleChange}
                      className="h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-600"
                    />
                    <label htmlFor="isDecisionMaker" className="ml-2 block text-sm text-neutral-900">
                      Decision Maker
                    </label>
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Social Links & Website Card */}
          <Card variant="bordered">
            <CardBody className="p-6 sm:p-8">
              <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                <div className="sm:col-span-6">
                  <h3 className="text-lg font-semibold leading-7 text-neutral-900">
                    Online Presence
                  </h3>
                  <p className="mt-1 text-sm leading-6 text-neutral-600">
                    Website and social media profiles
                  </p>
                </div>

                {/* Website */}
                <div className="sm:col-span-3">
                  <label htmlFor="website" className="block text-sm font-medium leading-6 text-neutral-900">
                    Website
                  </label>
                  <div className="mt-2">
                    <input
                      type="url"
                      name="website"
                      id="website"
                      className="block w-full rounded-lg border-0 py-2.5 px-3 text-neutral-900 shadow-sm ring-1 ring-inset ring-neutral-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                      value={formData.website}
                      onChange={handleChange}
                      placeholder="https://example.com"
                    />
                  </div>
                </div>

                {/* LinkedIn */}
                <div className="sm:col-span-3">
                  <label htmlFor="linkedinUrl" className="block text-sm font-medium leading-6 text-neutral-900">
                    LinkedIn URL
                  </label>
                  <div className="mt-2">
                    <input
                      type="url"
                      name="linkedinUrl"
                      id="linkedinUrl"
                      className="block w-full rounded-lg border-0 py-2.5 px-3 text-neutral-900 shadow-sm ring-1 ring-inset ring-neutral-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                      value={formData.linkedinUrl}
                      onChange={handleChange}
                      placeholder="https://linkedin.com/in/..."
                    />
                  </div>
                </div>

                {/* Twitter */}
                <div className="sm:col-span-3">
                  <label htmlFor="twitterUrl" className="block text-sm font-medium leading-6 text-neutral-900">
                    Twitter URL
                  </label>
                  <div className="mt-2">
                    <input
                      type="url"
                      name="twitterUrl"
                      id="twitterUrl"
                      className="block w-full rounded-lg border-0 py-2.5 px-3 text-neutral-900 shadow-sm ring-1 ring-inset ring-neutral-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                      value={formData.twitterUrl}
                      onChange={handleChange}
                      placeholder="https://twitter.com/..."
                    />
                  </div>
                </div>

                {/* Facebook */}
                <div className="sm:col-span-3">
                  <label htmlFor="facebookUrl" className="block text-sm font-medium leading-6 text-neutral-900">
                    Facebook URL
                  </label>
                  <div className="mt-2">
                    <input
                      type="url"
                      name="facebookUrl"
                      id="facebookUrl"
                      className="block w-full rounded-md border-0 py-2.5 px-3 text-neutral-900 shadow-sm ring-1 ring-inset ring-neutral-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                      value={formData.facebookUrl}
                      onChange={handleChange}
                      placeholder="https://facebook.com/..."
                    />
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Preferences & Compliance Card */}
          <Card variant="bordered">
            <CardBody className="p-6 sm:p-8">
              <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                <div className="sm:col-span-6">
                  <h3 className="text-lg font-semibold leading-7 text-neutral-900">
                    Preferences & Compliance
                  </h3>
                  <p className="mt-1 text-sm leading-6 text-neutral-600">
                    Communication preferences and compliance settings
                  </p>
                </div>

                {/* Preferred Contact Method */}
                <div className="sm:col-span-3">
                  <label htmlFor="preferredContactMethod" className="block text-sm font-medium leading-6 text-neutral-900">
                    Preferred Contact Method
                  </label>
                  <div className="mt-2">
                    <select
                      name="preferredContactMethod"
                      id="preferredContactMethod"
                      className="block w-full rounded-lg border-0 py-2.5 px-3 text-neutral-900 shadow-sm ring-1 ring-inset ring-neutral-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                      value={formData.preferredContactMethod}
                      onChange={handleChange}
                    >
                      <option value="">Select method</option>
                      <option value="EMAIL">Email</option>
                      <option value="PHONE">Phone</option>
                      <option value="SMS">SMS</option>
                      <option value="WHATSAPP">WhatsApp</option>
                    </select>
                  </div>
                </div>

                {/* Language Preference */}
                <div className="sm:col-span-3">
                  <label htmlFor="languagePreference" className="block text-sm font-medium leading-6 text-neutral-900">
                    Language Preference
                  </label>
                  <div className="mt-2">
                    <select
                      name="languagePreference"
                      id="languagePreference"
                      className="block w-full rounded-lg border-0 py-2.5 px-3 text-neutral-900 shadow-sm ring-1 ring-inset ring-neutral-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                      value={formData.languagePreference}
                      onChange={handleChange}
                    >
                      <option value="ARABIC">Arabic</option>
                      <option value="ENGLISH">English</option>
                    </select>
                  </div>
                </div>

                {/* Time Zone */}
                <div className="sm:col-span-3">
                  <label htmlFor="timeZone" className="block text-sm font-medium leading-6 text-neutral-900">
                    Time Zone
                  </label>
                  <div className="mt-2">
                    <input
                      type="text"
                      name="timeZone"
                      id="timeZone"
                      className="block w-full rounded-lg border-0 py-2.5 px-3 text-neutral-900 shadow-sm ring-1 ring-inset ring-neutral-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                      value={formData.timeZone}
                      onChange={handleChange}
                      placeholder="e.g., America/New_York"
                    />
                  </div>
                </div>

                {/* Compliance Checkboxes */}
                <div className="sm:col-span-6">
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        name="doNotContact"
                        id="doNotContact"
                        checked={formData.doNotContact}
                        onChange={handleChange}
                        className="h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-600"
                      />
                      <label htmlFor="doNotContact" className="ml-2 block text-sm text-neutral-900">
                        Do Not Contact
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        name="gdprConsent"
                        id="gdprConsent"
                        checked={formData.gdprConsent}
                        onChange={handleChange}
                        className="h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-600"
                      />
                      <label htmlFor="gdprConsent" className="ml-2 block text-sm text-neutral-900">
                        GDPR Consent
                      </label>
                    </div>

                    {formData.gdprConsent && (
                      <div className="ml-6">
                        <label htmlFor="gdprConsentDate" className="block text-sm font-medium leading-6 text-neutral-900">
                          GDPR Consent Date
                        </label>
                        <div className="mt-2">
                          <input
                            type="date"
                            name="gdprConsentDate"
                            id="gdprConsentDate"
                            className="block w-full rounded-md border-0 py-2.5 px-3 text-neutral-900 shadow-sm ring-1 ring-inset ring-neutral-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                            value={formData.gdprConsentDate}
                            onChange={handleChange}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Additional Information Card */}
          <Card variant="bordered">
            <CardBody className="p-6 sm:p-8">
              <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                <div className="sm:col-span-6">
                  <h3 className="text-lg font-semibold leading-7 text-neutral-900 flex items-center">
                    <DocumentTextIcon className="h-5 w-5 mr-2 text-neutral-400" />
                    Additional Information
                  </h3>
                  <p className="mt-1 text-sm leading-6 text-neutral-600">
                    Priority level and notes about this client
                  </p>
                </div>

                {/* Importance Level */}
                <div className="sm:col-span-3">
                  <label htmlFor="importance" className="block text-sm font-medium leading-6 text-neutral-900">
                    Priority Level
                  </label>
                  <div className="mt-2">
                    <select
                      name="importance"
                      id="importance"
                      className="block w-full rounded-md border-0 py-2.5 px-3 text-neutral-900 shadow-sm ring-1 ring-inset ring-neutral-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                      value={formData.importance}
                      onChange={handleChange}
                    >
                      <option value="VERY_LOW">Very Low</option>
                      <option value="LOW">Low</option>
                      <option value="MEDIUM">Medium</option>
                      <option value="HIGH">High</option>
                      <option value="VERY_HIGH">Very High</option>
                    </select>
                  </div>
                  <div className="mt-3">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getImportanceColor(formData.importance)}`}>
                      {formData.importance.replace('_', ' ')}
                    </span>
                  </div>
                </div>

                {/* Notes */}
                <div className="sm:col-span-6">
                  <label htmlFor="notes" className="block text-sm font-medium leading-6 text-neutral-900">
                    Notes
                  </label>
                  <div className="mt-2">
                    <textarea
                      name="notes"
                      id="notes"
                      rows={4}
                      className="block w-full rounded-lg border-0 py-2.5 px-3 text-neutral-900 shadow-sm ring-1 ring-inset ring-neutral-300 placeholder:text-neutral-400 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                      value={formData.notes}
                      onChange={handleChange}
                      placeholder="Additional notes about the client, preferences, or important details..."
                    />
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Form Actions */}
          <Card variant="bordered" className="sticky bottom-4">
            <CardBody className="p-4 sm:p-6">
              <div className="flex items-center justify-end gap-x-4">
                <Button
                  variant="ghost"
                  onClick={() => navigate(getClientPath('/clients'))}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  isLoading={saving}
                >
                  {isEdit ? 'Update Client' : 'Create Client'}
                </Button>
              </div>
            </CardBody>
          </Card>
        </form>
      </div>
    </div>
  );
};

export default ClientForm;
